import type { UseCaseConfig } from "../types";
import { CORE_FIELDS } from "../shared/form-fields";
import { USE_CASE_SEO } from "../seo-manifest";
import { hrefForUseCase } from "../links";
import { TS_CASE_STUDY_HREF } from "../case-studies/teachingStrategies";
import { CrossLink } from "../../components/landing/CrossLink";

/**
 * Customer Success use case (slug kept as `customer-marketing` so existing
 * links keep working). Initial copy is a first pass derived from the
 * customer-marketing form schema doc; refine before launch. The narrative
 * `reportId` is a placeholder — point it at the real sample narrative once
 * that's authored.
 */
export const customerMarketingConfig: UseCaseConfig = {
  slug: "customer-marketing",
  menuLabel: "Customer Success",
  navLabel: "Use Case · Customer Success",
  teaser:
    "One personalized narrative per account, built from the usage your customers already create.",
  meta: USE_CASE_SEO["customer-marketing"],
  hero: {
    eyebrowLabel: "Use Case",
    subhead: "Customer Success · B2B SaaS",
    headlineLead: "Their year with you, ",
    headlineEmphasis: "celebrated.",
    body: "They had a great year with you. Most of them can\u2019t see it. Forward turns the usage, milestones, and outcomes your customers already create into a personalized narrative, one per account, built around the moment that matters most.",
    byline: "One narrative per customer. Built for the moment that matters.",
  },
  narrativeSample: {
    reportId: "loopwire_northfield_2025_yir_p4n7k2",
    badgeLabel: "Sample",
    sampleTitle: "Sample customer Year in Review",
    eyebrow: "The format",
    captionHeadline: "A narrative per customer, in your voice.",
    captionBody:
      "Tap or swipe through a sample personalized narrative. Each section pairs a single headline number from the customer’s usage with the moments behind it, shaped to your brand. Built the same way for any milestone.",
    disclosure: (
      <>
        The sample shown is a Year in Review for <em>Northfield Marketing</em>,
        a fictional Loopwire customer, built to demonstrate the format. The same
        engine handles onboarding wraps, mid-cycle check-ins, renewal-eve recaps
        — any milestone. For a live campaign, see how{" "}
        <CrossLink href={TS_CASE_STUDY_HREF}>
          Teaching Strategies celebrated every program it serves
        </CrossLink>
        .
      </>
    ),
    fullScreenLabel: "Open the sample full screen →",
    ctaLabel: "Open sample",
  },
  scale: {
    eyebrow: "From a flagship account to your full book of business",
    leadIn: "Whether it’s your ",
    firstHighlight: "top ten accounts",
    middle: (
      <>
        {" "}
        or <br className="hidden md:block" />
      </>
    ),
    secondHighlight: "every customer in your CRM",
    tail: (
      <>
        ,
        <br />
        the same framework scales with you.
      </>
    ),
    subtitle: (
      <>
        Hand-curate a recap for your top renewal conversation. Or ship
        personalized narratives to every account in your CRM from the same data
        pipeline. Brand, voice, and pacing stay consistent.
      </>
    ),
    counters: ["10", "100", "1K", "10K", "100K", "All"],
    footnote: (
      <>
        Publishing customer research for marketing, too?{" "}
        <CrossLink href={hrefForUseCase("industry-reporting")} bold>
          Turn that into research that converts
        </CrossLink>
        .
      </>
    ),
  },
  moments: {
    eyebrow: "Where it lands",
    headline: "For every moment that should feel like more than an email.",
    items: [
      {
        title: "Onboarding",
        body: "First impressions that feel personal, not automated.",
      },
      {
        title: "Mid-cycle",
        body: "A health check that doesn’t feel like a check-in.",
      },
      {
        title: "Pre-renewal",
        body: "The case for staying, told through their own usage.",
      },
      {
        title: "Expansion",
        body: "Celebrate what they’ve earned. Hint at what’s next.",
      },
      {
        eyebrow: "Flagship",
        title: "Year in Review",
        body: "December’s biggest moment, told in their voice.",
      },
    ],
  },
  pillars: {
    eyebrow: "Built for Customer Success teams",
    headline: "Three reasons your team can actually pull this off.",
    points: [
      {
        title: "No integration. No IT. No engineering.",
        body: "A CSV export of the data you already have is the input. Your data team doesn’t need to be in the room. Your engineering team doesn’t need a ticket.",
      },
      {
        title: "Typically 4–8 weeks to launch.",
        body: "From signed scope to your first campaign, typically 4–8 weeks. Once live, run it at whatever cadence fits — weekly, monthly, quarterly — the engine keeps going.",
      },
      {
        title: "Not mail-merge.",
        body: 'A real narrative engine — animated, brand-shaped, one narrative per account. Looks great on a phone and just as good on desktop. The opposite of the "your year in numbers" email that gets archived in three seconds.',
      },
    ],
  },
  cta: {
    sectionEyebrow: "Your customers",
    headlineLead: "What would ",
    headlineEmphasis: "your customers’",
    headlineTail: " milestone feel like told back to them?",
    body: "Tell us about your customers and the moment you care about most. We’ll build a sample narrative for that exact lifecycle gap, no demo, no pitch.",
    submitLabel: "Send it →",
    sendingLabel: "Sending…",
    continueLabel: "Continue →",
    success: {
      headline: "We’ll be in touch.",
      body: "Someone from our team will reach out within one to two business days with a sample narrative built around the space and lifecycle moment you described.",
    },
    fields: [
      ...CORE_FIELDS,
      {
        key: "customer_space",
        type: "select",
        label: "What space are your customers in?",
        required: true,
        core: false,
        allowOther: true,
        options: [
          { value: "saas", label: "SaaS" },
          { value: "marketplace", label: "Marketplace" },
          { value: "fintech", label: "Fintech" },
          { value: "edtech", label: "EdTech" },
          { value: "healthtech", label: "HealthTech" },
          { value: "wellness", label: "Wellness" },
        ],
      },
      {
        key: "lifecycle_gap",
        type: "select",
        label:
          "Where in the customer lifecycle is your biggest engagement gap?",
        required: true,
        core: false,
        options: [
          { value: "first-90-days", label: "First 90 days" },
          { value: "mid-year", label: "Mid-Year" },
          { value: "renewal", label: "Renewal" },
          { value: "expansion", label: "Expansion" },
        ],
      },
      {
        key: "customer_wins",
        type: "text",
        label:
          "In one sentence, what does a “great year” look like for your customer?",
        placeholder:
          "e.g. Their team adopted the workflow company-wide and stopped using the legacy tool.",
        required: true,
        core: false,
      },
    ],
  },
};
